import {combineLatest, Observable, Subject} from 'rxjs';
import {debounce, delay, distinctUntilChanged, filter, map} from 'rxjs/operators';

import {readApiModel} from '../../libs/shared/core/src/helpers/api/apiHelpers';
import {globalWindow} from '../../libs/shared/core/src/helpers/browser/browserHelpers';
import {addQueryToPath} from '../../libs/shared/core/src/helpers/browser/urlHelpers';
import {TaskSourcesEnum} from '../../libs/shared/core/src/helpers/instrumentation/eventLoopPerformanceHelpers';
import {fromAnimationFrameOrHiddenCallback} from '../../libs/shared/core/src/helpers/rx/fromAnimationFrame';
import {fromVisibilityEvent} from '../../libs/shared/core/src/helpers/rx/visibility';
import {ExtendedError} from '../../libs/shared/core/src/helpers/types/customErrors';
import {CompanyModel} from '../../libs/shared/core/src/models/api/company/companyModel';
import {TeammateModel} from '../../libs/shared/core/src/models/api/teammateModel';
import {reportToBugsnag} from '../../libs/shared/core/src/services/bugsnag/bugsnag';
import {fetchAsync} from '../../libs/shared/core/src/services/http/http';
import {HttpError} from '../../libs/shared/core/src/services/http/httpError';
import {ApiConversationDataGroup, DataGroupModel} from '../../src/models/api/dataGroupModel';
import {PrecogStatusTypesEnum} from '../../src/models/api/precogModel';
import {PushConfigModel} from '../../src/models/api/pushConfigModel';
import {debounceIf} from '../rx/debounceIf';
import {distinctTrailingThrottleTime} from '../rx/distinctTrailingThrottleTime';
import {PushDispatchProps} from './desktopPushEventSubscriptions';
import {MobilePushDispatchProps} from './mobilePushEventSubscriptions';
import {PushTaskQueue} from './pushTaskQueue';

/*
 * Constants.
 */

/** Throttle fetching updated conversations to filter out redundant push events. */
export const fetchUpdatedConversationThrottleDuration = 200;

/** Duration to wait after push disconnect until showing the offline banner. */
const offlineBannerPushDisconnectDelay = 15 * 1000;

/*
 * Enums.
 */

export enum PushLifecycleEventsEnum {
  CONNECT = 'CONNECT',
  DISCONNECT = 'DISCONNECT',
  RECONNECT = 'RECONNECT',
  PING = 'PING',
}

/*
 * Types.
 */

export interface PushBaseProps {
  pusherConfiguration: PushConfigModel;
  company: CompanyModel;
  user: TeammateModel;
  cellsPanelsRooms: ReadonlyArray<string>;
  viewerPanelsConversationIds: ReadonlyArray<number>;
  isNotificationCenterEnabled: boolean;
  shouldAlwaysSubscribeToUserFolderRoom: boolean;
}

export interface FetchUpdatedConversationOptions {
  conversationId: number;
  conversationUrl: string;
  shouldDiscardServerPrecog: boolean;
  requesterId: number | undefined;
}

export type SharedPushDispatchProps = PushDispatchProps | MobilePushDispatchProps;

export type UpdatedConversationRenderCallback = (
  conversationId: number,
  dataGroup: DataGroupModel,
  requesterId?: number,
) => void;

/*
 * Fetch updated conversation helpers.
 */

export function makeFetchUpdatedConversationTasksSubject(
  fetchUpdatedConversationCallback: (options: FetchUpdatedConversationOptions) => void,
) {
  const fetchUpdatedConversationTasks$ = new Subject<FetchUpdatedConversationOptions>();
  fetchUpdatedConversationTasks$
    .pipe(
      distinctTrailingThrottleTime(
        fetchUpdatedConversationThrottleDuration,
        (options) => `${options.conversationId}-${options.shouldDiscardServerPrecog}-${options.requesterId}`,
      ),
    )
    .subscribe(
      (options) => {
        fetchUpdatedConversationCallback(options);
      },
      (error) => {
        reportToBugsnag(new ExtendedError('Fetch updated conversation pipeline broken', error));
      },
      () => {
        reportToBugsnag(new Error('Fetch updated conversation pipeline unexpectedly completed'));
      },
    );
  return fetchUpdatedConversationTasks$;
}

export function fetchUpdatedConversation(
  processingQueue: PushTaskQueue,
  options: FetchUpdatedConversationOptions,
  renderCallback: UpdatedConversationRenderCallback,
) {
  const {conversationId, conversationUrl, shouldDiscardServerPrecog, requesterId} = options;

  fetchAsync(addQueryToPath(conversationUrl, {light: true})).then(
    (response) => {
      // Enqueue a new task to decode and render.
      processingQueue.enqueue(() => {
        const dataGroup = readApiModel(ApiConversationDataGroup, response);

        const conversationTypingPrecogs =
          dataGroup.conversationPrecogs &&
          dataGroup.conversationPrecogs.filter((p) => p.status === PrecogStatusTypesEnum.TYPING);
        const conversationSentPrecogs =
          dataGroup.conversationPrecogs &&
          dataGroup.conversationPrecogs.filter((p) => p.status === PrecogStatusTypesEnum.SENT);

        let newDataGroup: DataGroupModel = {
          ...dataGroup,
          conversationPrecogs: conversationTypingPrecogs,
          conversationSentPrecogs,
        };

        // Setting conversationPrecogs to undefined makes them ignored by the reducer.
        // We always keep sentPrecogs because we only receive them from the server.
        if (shouldDiscardServerPrecog) {
          newDataGroup = {
            ...newDataGroup,
            conversationPrecogs: undefined,
          };
        }

        return () => renderCallback(conversationId, newDataGroup, requesterId);
      });
    },
    (error) => {
      if (error instanceof HttpError) {
        return;
      }
      reportToBugsnag(error);
    },
  );
}

/*
 * Push connectivity helpers.
 */

export function buildOfflineStateObservable(
  isOnline$: Observable<boolean>,
  isPushDisconnected$: Observable<boolean>,
): Observable<boolean> {
  const hasPushBeenDisconnected$ = isPushDisconnected$.pipe(
    // Debounce if pusher is disconnected.
    debounceIf((isPushDisconnected) => isPushDisconnected, offlineBannerPushDisconnectDelay),
  );

  return combineLatest([isOnline$, hasPushBeenDisconnected$]).pipe(
    map(([isOnline, hasPushBeenDisconnected]) => !isOnline || hasPushBeenDisconnected),
    distinctUntilChanged(),
    debounce(() => fromAnimationFrameOrHiddenCallback({source: TaskSourcesEnum.PUSHER})),
  );
}

export function buildPushReconnectionObservable() {
  const visibilityEvents$ = fromVisibilityEvent(globalWindow).pipe(
    delay(0),
    filter((hidden) => !hidden),
    map(() => PushLifecycleEventsEnum.RECONNECT),
  );

  return visibilityEvents$;
}
